import { Checkbox, Grid, Select, Switch, Text } from '@mantine/core';

import { CARBON_ANALYTIC_OPTIONS, METER_STR } from 'constants/projects';
import { NONE } from 'constants/variables';

import InputAndLabelGridRow from 'common/Components/InputAndLabelGridRow';

import { useProjectsTranslations } from '../hooks';
import { useSamplePlanFormContext } from '../samplePlanFormContextV2';
import { getDensityOptionsFromConfig } from '../utils';

import { useCarbonPlanConfigQuery } from './queries';

const MeterCoreSampleDetails = () => {
  const translations = useProjectsTranslations();

  const samplePlanForm = useSamplePlanFormContext();
  const formValues = samplePlanForm.getValues();

  const { samplePlanConfig } = useCarbonPlanConfigQuery();
  const { meter_core } = samplePlanConfig || {};

  const splitOptions = [
    { label: translations.doNotSplit, value: NONE },
    ...(meter_core?.splits.map((split) => ({ label: split.label, value: split.label })) || []),
  ];

  return (
    <Grid mt="md">
      <InputAndLabelGridRow
        elemId="meter-core-details-switch"
        label={translations.meterCoreSampleDetails}
        fw="bold"
        size="xl"
        span={5}
        input={
          <Switch
            id="meter-core-details-switch"
            {...samplePlanForm.getInputProps('isMeterCore', { type: 'checkbox' })}
            checked={formValues.isMeterCore}
            onChange={() => samplePlanForm.setFieldValue('isMeterCore', !formValues.isMeterCore)}
            disabled={!samplePlanConfig?.depths.includes(METER_STR)}
          />
        }
      />
      {formValues.isMeterCore && (
        <>
          <InputAndLabelGridRow
            elemId="meter-core-lab"
            label={translations.lab}
            span={5}
            input={
              <Select
                data={meter_core?.labs}
                id="meter-core-lab"
                {...samplePlanForm.getInputProps('mc_dict.lab')}
                value={formValues.mc_dict.lab}
                onChange={(value) => value && samplePlanForm.setFieldValue('mc_dict.lab', value)}
              />
            }
          />
          <InputAndLabelGridRow
            elemId="meter-core-analytic-select"
            label={translations.analytics}
            span={5}
            input={
              <Grid align="center">
                <Grid.Col span={5}>
                  <Select
                    data={CARBON_ANALYTIC_OPTIONS}
                    id="meter-core-analytic-select"
                    {...samplePlanForm.getInputProps('mc_dict.sic')}
                    value={formValues.mc_dict.sic}
                    onChange={(value) =>
                      value && samplePlanForm.setFieldValue('mc_dict.sic', value)
                    }
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <Checkbox
                    {...samplePlanForm.getInputProps('mc_dict.add_texture_ph', {
                      type: 'checkbox',
                    })}
                    checked={formValues.mc_dict.add_texture_ph}
                    disabled={meter_core?.add_texture_and_ph.required}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs">{translations.addTexturePhMsg}</Text>
                </Grid.Col>
              </Grid>
            }
          />
          <InputAndLabelGridRow
            elemId="meter-core-density-select"
            label={translations.density}
            span={5}
            input={
              <Select
                data={getDensityOptionsFromConfig(meter_core?.densities || [])}
                id="meter-core-density-select"
                allowDeselect={false}
                {...samplePlanForm.getInputProps('mc_dict.density')}
                value={String(formValues.mc_dict.density)}
              />
            }
          />
          {formValues.mc_dict.add_texture_ph && (
            <InputAndLabelGridRow
              elemId="meter-core-texture-density-select"
              label={translations.textureDensity}
              span={5}
              input={
                <Grid align="center">
                  <Grid.Col span={5}>
                    <Select
                      data={meter_core?.texture_densities || []}
                      id="meter-core-texture-density-select"
                      allowDeselect={false}
                      {...samplePlanForm.getInputProps('mc_dict.texture_density')}
                      value={formValues.mc_dict.texture_density}
                      onChange={(value) =>
                        value && samplePlanForm.setFieldValue('mc_dict.texture_density', value)
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={7}>
                    <Text size="xs">{translations.ensureOneSamplePerThousandMsg}</Text>
                  </Grid.Col>
                </Grid>
              }
            />
          )}
          <InputAndLabelGridRow
            elemId="meter-core-splits"
            label={translations.splits}
            span={5}
            input={
              <Select
                data={splitOptions}
                id="meter-core-splits"
                {...samplePlanForm.getInputProps('mc_dict.splits')}
                onChange={(value) => value && samplePlanForm.setFieldValue('mc_dict.splits', value)}
                value={formValues.mc_dict.splits}
              />
            }
          />
          <InputAndLabelGridRow
            elemId="meter-core-min-1-per-field"
            label={translations.min1PerField}
            span={5}
            input={
              <Checkbox
                id="meter-core-min-1-per-field"
                {...samplePlanForm.getInputProps('mc_dict.field_requirement', { type: 'checkbox' })}
                checked={formValues.mc_dict.field_requirement}
              />
            }
          />
          <InputAndLabelGridRow
            elemId="meter-core-cold-storage"
            label={`${translations.coldStorage}?`}
            span={5}
            input={
              <Checkbox
                id="meter-core-cold-storage"
                {...samplePlanForm.getInputProps('mc_dict.frozen_storage', { type: 'checkbox' })}
                checked={formValues.mc_dict.frozen_storage}
              />
            }
          />
        </>
      )}
    </Grid>
  );
};

export default MeterCoreSampleDetails;
